import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import LegalFooterBannerAndLinks from "components/legal/LegalFooterBannerAndLinks";
import LegalPagesBrandHeader from "components/legal/LegalPagesBrandHeader";
import { clientSafeMessage } from "utils/clientSafeMessage";

const performanceHighlights = [
  {
    label: "Transparency Score",
    value: "100%",
    detail: "Every closed cycle is documented with context and outcome.",
  },
  {
    label: "Update Rhythm",
    value: "Daily",
    detail: "Performance reporting is updated frequently for active clients.",
  },
  {
    label: "Execution Priority",
    value: "Risk-first",
    detail: "Capital protection and controlled drawdown remain top priorities.",
  },
];

const proofCards = [
  {
    title: "Verified Cycle Performance",
    text: "Track closed cycles with clear profit/loss summaries and timing windows.",
  },
  {
    title: "Risk & Drawdown View",
    text: "Review drawdown behavior and how risk settings affect account outcomes.",
  },
  {
    title: "Execution Consistency",
    text: "Follow fill quality and execution stability across market conditions.",
  },
];

const whatYouCanReview = [
  "Closed-trade outcomes and cycle-level performance",
  "Profit behavior with realistic risk expectations",
  "Drawdown management and recovery phases",
  "Account-level consistency over time",
];

const periodOrder = ["daily", "weekly", "monthly", "yearly"];
/**
 * En dev intégré (setupProxy), l’API est sur le même port que le front (ex. 3002).
 * Laisser vide = URLs relatives `/api/...` → pas besoin d’ouvrir :5000 dans le navigateur.
 * En prod, définir REACT_APP_API_URL vers l’hôte API (HTTPS, sans slash final).
 */
const apiBaseUrl = (process.env.REACT_APP_API_URL || "").trim();

function sanitizeClientVisibleCopy(text) {
  if (text == null || typeof text !== "string") return text;
  const brokerSafe = text.replace(/ctrader/gi, "broker");
  return clientSafeMessage(brokerSafe, brokerSafe);
}

function responseIsJson(contentType) {
  const c = (contentType || "").toLowerCase();
  return c.includes("application/json") || c.includes("text/json");
}

function formatValue(value, suffix = "") {
  if (value == null || Number.isNaN(value)) return "N/A";
  return `${value}${suffix}`;
}

/** Même format que MasterResult.html : +1.23 / -1.23, gras. */
function formatPnlUsd(value) {
  if (value == null || Number.isNaN(Number(value))) return null;
  const v = Number(value);
  const sign = v > 0 ? "+" : "";
  return `${sign}${v.toFixed(2)}`;
}

function pad2(n) {
  return String(n).padStart(2, "0");
}

/** Aligné sur `periodReturnPercent` backend : rendement approximatif vs solde courant. */
function periodReturnApprox(balanceUsd, periodPnlUsd) {
  if (!Number.isFinite(periodPnlUsd)) return null;
  const b = Number(balanceUsd);
  if (!Number.isFinite(b) || b === 0) return null;
  const startApprox = b - periodPnlUsd;
  if (startApprox <= 1e-6) return null;
  return Number(((periodPnlUsd / startApprox) * 100).toFixed(2));
}

/** Max drawdown % — même logique que l’API (chemin d’équité depuis solde − Σ PnL période). */
function maxDrawdownPercentFromBalanceAndPnls(balanceNow, pnlsOrdered) {
  const pnls = pnlsOrdered || [];
  const periodPnl = pnls.reduce((s, x) => s + (Number(x) || 0), 0);
  const startEquity = Number(balanceNow) - periodPnl;
  if (
    !Number.isFinite(Number(balanceNow)) ||
    !Number.isFinite(startEquity) ||
    startEquity <= 1e-6
  ) {
    return null;
  }
  let equity = startEquity;
  let peak = equity;
  let maxDd = 0;
  for (const raw of pnls) {
    equity += Number(raw) || 0;
    if (equity > peak) peak = equity;
    if (peak > 1e-6) {
      const dd = ((peak - equity) / peak) * 100;
      if (dd > maxDd) maxDd = dd;
    }
  }
  return Number(maxDd.toFixed(2));
}

/** Trades fermés sur une ligne journalière : aligne le total sur W+L si `trades_count` est en retard. */
function closedTradesFromDailyRow(r) {
  const tcRaw = Number(r?.trades_count);
  const wi = Number(r?.wins_count) || 0;
  const lo = Number(r?.losses_count) || 0;
  const fromWl = wi + lo;
  const fromTc =
    Number.isFinite(tcRaw) && tcRaw >= 0 ? Math.floor(tcRaw) : 0;
  return Math.max(fromTc, fromWl);
}

function ymdUTC(y, m0, d) {
  return `${y}-${pad2(m0 + 1)}-${pad2(d)}`;
}

function monthKeyUtc(o) {
  return o.y * 12 + o.m0;
}

function utcMonthFromKey(k) {
  return { y: Math.floor(k / 12), m0: k % 12 };
}

function parseYmdParts(s) {
  if (!s || typeof s !== "string") return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s.trim());
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]) - 1;
  const d = Number(m[3]);
  if (mo < 0 || mo > 11 || d < 1 || d > 31) return null;
  return { y, m0: mo, d };
}

/** Premier mois calendaire UTC où il y a au moins 1 trade fermé ; sinon mois avec pips/PnL. */
function firstMonthWithTradeActivityUtc(dailyRows) {
  let bestK = null;
  for (const r of dailyRows || []) {
    if (closedTradesFromDailyRow(r) < 1) continue;
    const p = parseYmdParts(r.trading_date);
    if (!p) continue;
    const k = monthKeyUtc({ y: p.y, m0: p.m0 });
    if (bestK == null || k < bestK) bestK = k;
  }
  if (bestK != null) return utcMonthFromKey(bestK);
  for (const r of dailyRows || []) {
    const hasPips =
      (Number(r.pips_positive) || 0) !== 0 ||
      (Number(r.pips_negative) || 0) !== 0;
    const hasPnl = Math.abs(Number(r.pnl_usd) || 0) > 1e-9;
    if (!hasPips && !hasPnl) continue;
    const p = parseYmdParts(r.trading_date);
    if (!p) continue;
    const k = monthKeyUtc({ y: p.y, m0: p.m0 });
    if (bestK == null || k < bestK) bestK = k;
  }
  return bestK != null ? utcMonthFromKey(bestK) : null;
}

/** Dernier mois UTC avec activité (pour ouvrir Results sur des données réelles). */
function latestMonthWithTradeActivityUtc(dailyRows) {
  let bestK = null;
  for (const r of dailyRows || []) {
    if (closedTradesFromDailyRow(r) < 1) continue;
    const p = parseYmdParts(r.trading_date);
    if (!p) continue;
    const k = monthKeyUtc({ y: p.y, m0: p.m0 });
    if (bestK == null || k > bestK) bestK = k;
  }
  if (bestK != null) return utcMonthFromKey(bestK);
  for (const r of dailyRows || []) {
    const hasPips =
      (Number(r.pips_positive) || 0) !== 0 ||
      (Number(r.pips_negative) || 0) !== 0;
    const hasPnl = Math.abs(Number(r.pnl_usd) || 0) > 1e-9;
    if (!hasPips && !hasPnl) continue;
    const p = parseYmdParts(r.trading_date);
    if (!p) continue;
    const k = monthKeyUtc({ y: p.y, m0: p.m0 });
    if (bestK == null || k > bestK) bestK = k;
  }
  return bestK != null ? utcMonthFromKey(bestK) : null;
}

function monthHasTradeActivityUtc(dailyRows, month) {
  if (!month) return false;
  for (const r of dailyRows || []) {
    if (closedTradesFromDailyRow(r) < 1) continue;
    const p = parseYmdParts(r.trading_date);
    if (!p) continue;
    if (p.y === month.y && p.m0 === month.m0) return true;
  }
  return false;
}

function formatMonthYearUtcLabel(y, m0) {
  const raw = new Date(Date.UTC(y, m0, 1)).toLocaleDateString("fr-FR", {
    month: "long",
    year: "numeric",
  });
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

function currentUtcMonth() {
  const n = new Date();
  return { y: n.getUTCFullYear(), m0: n.getUTCMonth() };
}

/** Tous les jours du mois UTC (1 → 28/29/30/31). */
function allDayKeysUtcMonth(y, m0) {
  const last = new Date(Date.UTC(y, m0 + 1, 0)).getUTCDate();
  const keys = [];
  for (let day = 1; day <= last; day++) {
    keys.push(ymdUTC(y, m0, day));
  }
  return keys;
}

/**
 * Plage UTC pour le tableau : 12 mois glissants, 5 ans pour l’annuel,
 * et jusqu’à 10 ans en retrait pour permettre Daily/Weekly sur les mois passés.
 */
function supabaseDailyFetchRangeUtc() {
  const end = new Date();
  const y = end.getUTCFullYear();
  const m0 = end.getUTCMonth();
  const lastD = new Date(Date.UTC(y, m0 + 1, 0)).getUTCDate();
  const toStr = ymdUTC(y, m0, lastD);

  const monthlyStart = new Date(Date.UTC(y, m0, 1));
  monthlyStart.setUTCMonth(monthlyStart.getUTCMonth() - 11);
  const yearlyStart = new Date(Date.UTC(y - 4, 0, 1));
  const decadeStart = new Date(Date.UTC(y - 10, 0, 1));
  const fromTime = Math.min(
    monthlyStart.getTime(),
    yearlyStart.getTime(),
    decadeStart.getTime(),
  );
  const fromD = new Date(fromTime);
  const fromStr = ymdUTC(
    fromD.getUTCFullYear(),
    fromD.getUTCMonth(),
    fromD.getUTCDate(),
  );
  return { fromStr, toStr };
}

/** True si le bloc « yearly » du summary n’a aucune métrique exploitable (ex. pas de ligne en base). */
function yearlySummaryFromApiIsEmpty(p) {
  if (!p) return true;
  const trades = p.trades;
  const hasTrades = trades != null && Number(trades) > 0;
  const pnl = p.pnlUsd;
  const hasPnl = pnl != null && Number.isFinite(Number(pnl)) && Number(pnl) !== 0;
  const pp = p.pipsPositive;
  const pn = p.pipsNegative;
  const hasPips =
    (pp != null && Number(pp) !== 0) || (pn != null && Number(pn) !== 0);
  const wr = p.winRate;
  const hasWin = wr != null && Number.isFinite(Number(wr));
  return !hasTrades && !hasPnl && !hasPips && !hasWin;
}

/**
 * Cartes du haut pour Yearly quand le summary ne fournit pas yearly : même agrégation YTD
 * que le tableau (année civile UTC, somme des lignes journalières).
 * `balanceUsd` vient du summary API (solde courtier) pour le PnL % ; la somme des `pnl_usd` sert à ce % uniquement.
 */
/**
 * Stats for the current calendar day (UTC) — used when Daily is selected.
 */
function todayOverviewFromDailyRows(dailyRows, balanceUsd) {
  const n = new Date();
  const today = ymdUTC(n.getUTCFullYear(), n.getUTCMonth(), n.getUTCDate());
  const r = (dailyRows || []).find((row) => row?.trading_date === today) || null;
  const pPos = Number(r?.pips_positive) || 0;
  const pNeg = Number(r?.pips_negative) || 0;
  const pnlSum = Number(r?.pnl_usd) || 0;
  const t = closedTradesFromDailyRow(r);
  const w = Number(r?.wins_count) || 0;
  const winRate = t > 0 ? Number(((w / t) * 100).toFixed(2)) : null;
  const pnlPercent = periodReturnApprox(balanceUsd, pnlSum);
  const drawdownPercent =
    balanceUsd != null
      ? maxDrawdownPercentFromBalanceAndPnls(balanceUsd, [pnlSum])
      : null;
  return {
    pnlPercent: pnlPercent ?? 0,
    pnlUsd: Number(pnlSum.toFixed(2)),
    drawdownPercent: drawdownPercent ?? 0,
    winRate,
    trades: t,
    pipsPositive: Number(pPos.toFixed(2)),
    pipsNegative: Number(pNeg.toFixed(2)),
    periodLabel: `Today ${today} (UTC)`,
    updatedAt: r?.updated_at || new Date().toISOString(),
    source: "Daily (today)",
  };
}

function yearlyOverviewFromDailyRows(dailyRows, balanceUsd) {
  const y = String(new Date().getUTCFullYear());
  let pPos = 0;
  let pNeg = 0;
  let pnlSum = 0;
  let t = 0;
  let w = 0;
  let l = 0;
  for (const r of dailyRows || []) {
    if (!r?.trading_date?.startsWith(y)) continue;
    pPos += Number(r.pips_positive) || 0;
    pNeg += Number(r.pips_negative) || 0;
    pnlSum += Number(r.pnl_usd) || 0;
    t += closedTradesFromDailyRow(r);
    w += Number(r.wins_count) || 0;
    l += Number(r.losses_count) || 0;
  }
  if (
    t === 0 &&
    pPos === 0 &&
    pNeg === 0 &&
    Math.abs(pnlSum) < 1e-9
  ) {
    return null;
  }
  const winRate = t > 0 ? Number(((w / t) * 100).toFixed(2)) : null;
  const pnlPercent = periodReturnApprox(balanceUsd, pnlSum);
  const yRows = (dailyRows || [])
    .filter((r) => r?.trading_date?.startsWith(y))
    .sort((a, b) =>
      String(a.trading_date).localeCompare(String(b.trading_date)),
    );
  const pnlsSeq = yRows.map((r) => Number(r.pnl_usd) || 0);
  const drawdownPercent =
    balanceUsd != null
      ? maxDrawdownPercentFromBalanceAndPnls(balanceUsd, pnlsSeq)
      : null;
  return {
    pnlPercent,
    pnlUsd: null,
    drawdownPercent,
    winRate,
    trades: t,
    pipsPositive: Number(pPos.toFixed(2)),
    pipsNegative: Number(pNeg.toFixed(2)),
    periodLabel: `Calendar year ${y} (UTC), from synced daily rows`,
    updatedAt: null,
    source: "Daily aggregate",
  };
}

function buildPerformanceTableRows(activePeriod, dailyRows, viewMonthUtc) {
  const byDate = new Map((dailyRows || []).map((r) => [r.trading_date, r]));
  const vm = viewMonthUtc || currentUtcMonth();
  const y = vm.y;
  const m0 = vm.m0;

  if (activePeriod === "daily") {
    const keys = allDayKeysUtcMonth(y, m0);
    const rows = keys.map((date) => {
      const r = byDate.get(date);
      if (r && closedTradesFromDailyRow(r) > 0) {
        return { ...r, rowKey: date, periodLabel: date };
      }
      return {
        rowKey: date,
        periodLabel: date,
        trading_date: date,
        pips_positive: null,
        pips_negative: null,
        trades_count: null,
        wins_count: null,
        losses_count: null,
        win_rate_percent: null,
        pnl_usd: null,
      };
    });
    return { mode: "daily", rows };
  }

  if (activePeriod === "weekly") {
    const last = new Date(Date.UTC(y, m0 + 1, 0)).getUTCDate();
    const rows = [];
    for (let start = 1; start <= last; start += 7) {
      const endD = Math.min(start + 6, last);
      let pPos = 0;
      let pNeg = 0;
      let t = 0;
      let w = 0;
      let l = 0;
      let pnl = 0;
      for (let day = start; day <= endD; day++) {
        const dt = new Date(Date.UTC(y, m0, day));
        const wd = dt.getUTCDay();
        if (wd === 0 || wd === 6) continue;
        const r = byDate.get(ymdUTC(y, m0, day));
        if (r) {
          pPos += Number(r.pips_positive) || 0;
          pNeg += Number(r.pips_negative) || 0;
          t += closedTradesFromDailyRow(r);
          w += Number(r.wins_count) || 0;
          l += Number(r.losses_count) || 0;
          pnl += Number(r.pnl_usd) || 0;
        }
      }
      const wr = t > 0 ? Number(((w / t) * 100).toFixed(2)) : null;
      rows.push({
        rowKey: `w-${y}-${m0}-${start}-${endD}`,
        periodLabel: `Week ${rows.length + 1} (days ${start}–${endD})`,
        pips_positive: pPos,
        pips_negative: pNeg,
        trades_count: t,
        wins_count: w,
        losses_count: l,
        win_rate_percent: wr,
        pnl_usd: Number(pnl.toFixed(2)),
      });
    }
    return { mode: "weekly", rows };
  }

  if (activePeriod === "monthly") {
    const monthMap = new Map();
    for (const r of dailyRows || []) {
      if (!r?.trading_date) continue;
      const ym = r.trading_date.slice(0, 7);
      if (!monthMap.has(ym)) {
        monthMap.set(ym, { pPos: 0, pNeg: 0, t: 0, w: 0, l: 0, pnl: 0 });
      }
      const a = monthMap.get(ym);
      a.pPos += Number(r.pips_positive) || 0;
      a.pNeg += Number(r.pips_negative) || 0;
      a.t += closedTradesFromDailyRow(r);
      a.w += Number(r.wins_count) || 0;
      a.l += Number(r.losses_count) || 0;
      a.pnl += Number(r.pnl_usd) || 0;
    }
    const ymKeys = [...monthMap.keys()].sort();
    const rows = ymKeys.map((ym) => {
      const [yy, mm] = ym.split("-").map(Number);
      const rawLabel = new Date(Date.UTC(yy, mm - 1, 1)).toLocaleDateString(
        "fr-FR",
        { month: "long", year: "numeric" },
      );
      const label = rawLabel.charAt(0).toUpperCase() + rawLabel.slice(1);
      const a = monthMap.get(ym);
      const trades = a.t;
      const wr = trades > 0 ? Number(((a.w / trades) * 100).toFixed(2)) : null;
      return {
        rowKey: ym,
        periodLabel: label,
        pips_positive: a.pPos,
        pips_negative: a.pNeg,
        trades_count: trades,
        wins_count: a.w,
        losses_count: a.l,
        win_rate_percent: wr,
        pnl_usd: Number(a.pnl.toFixed(2)),
      };
    });
    return { mode: "monthly", rows };
  }

  if (activePeriod === "yearly") {
    const yearMap = new Map();
    for (const r of dailyRows || []) {
      if (!r?.trading_date) continue;
      const yyyy = r.trading_date.slice(0, 4);
      if (!/^\d{4}$/.test(yyyy)) continue;
      if (!yearMap.has(yyyy)) {
        yearMap.set(yyyy, { pPos: 0, pNeg: 0, t: 0, w: 0, l: 0, pnl: 0 });
      }
      const a = yearMap.get(yyyy);
      a.pPos += Number(r.pips_positive) || 0;
      a.pNeg += Number(r.pips_negative) || 0;
      a.t += closedTradesFromDailyRow(r);
      a.w += Number(r.wins_count) || 0;
      a.l += Number(r.losses_count) || 0;
      a.pnl += Number(r.pnl_usd) || 0;
    }
    const yearKeys = [...yearMap.keys()].sort();
    const rows = yearKeys.map((yyyy) => {
      const a = yearMap.get(yyyy);
      const trades = a.t;
      const wr = trades > 0 ? Number(((a.w / trades) * 100).toFixed(2)) : null;
      return {
        rowKey: yyyy,
        periodLabel: yyyy,
        pips_positive: a.pPos,
        pips_negative: a.pNeg,
        trades_count: trades,
        wins_count: a.w,
        losses_count: a.l,
        win_rate_percent: wr,
        pnl_usd: Number(a.pnl.toFixed(2)),
      };
    });
    return { mode: "yearly", rows };
  }

  return { mode: "daily", rows: [] };
}

export default function Results() {
  const [activePeriod, setActivePeriod] = useState("daily");
  const [summary, setSummary] = useState(null);
  const [masters, setMasters] = useState([]);
  const [activeMasterId, setActiveMasterId] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [dailyRows, setDailyRows] = useState([]);
  const [dailyLoading, setDailyLoading] = useState(true);
  const [dailyMessage, setDailyMessage] = useState("");
  /** Mois UTC affiché pour les tableaux Daily / Weekly (navigation mois passés). */
  const [tableMonthUtc, setTableMonthUtc] = useState(currentUtcMonth);
  const didAutoJumpMonthRef = useRef(false);

  const activeMaster = useMemo(() => {
    if (!masters.length) return null;
    return (
      masters.find((m) => m.masterId === activeMasterId) ||
      masters.find((m) => m.published) ||
      masters[0]
    );
  }, [masters, activeMasterId]);

  const hasDailyTradeData = useMemo(
    () => (dailyRows || []).some((r) => closedTradesFromDailyRow(r) >= 1),
    [dailyRows],
  );
  const resultsAreVisible = Boolean(
    activeMaster?.published || hasDailyTradeData || summary?.published,
  );

  useEffect(() => {
    let isMounted = true;
    async function loadSummary() {
      try {
        setIsLoading(true);
        setError("");
        const res = await fetch(`${apiBaseUrl}/api/results/summary`);
        const contentType = res.headers.get("content-type") || "";
        if (!responseIsJson(contentType)) {
          throw new Error(
            "Results are temporarily unavailable. Please refresh the page and try again.",
          );
        }
        const data = await res.json();
        if (!res.ok || !data?.ok) {
          throw new Error(data?.message || "Unable to load results summary.");
        }
        if (isMounted) {
          setSummary(data);
          const rows = Array.isArray(data?.masters) ? data.masters : [];
          setMasters(rows);
          setActiveMasterId((prev) => {
            if (prev && rows.some((m) => m.masterId === prev)) return prev;
            const preferred =
              rows.find((m) => m.masterId === data?.activeMasterId) ||
              rows.find((m) => m.published) ||
              rows[0];
            return preferred?.masterId || "";
          });
        }
      } catch (err) {
        if (isMounted) {
          setError(
            clientSafeMessage(err?.message, "Unable to load results summary."),
          );
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }
    loadSummary();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    async function loadDaily() {
      try {
        setDailyLoading(true);
        setDailyMessage("");
        const { fromStr, toStr } = supabaseDailyFetchRangeUtc();
        const params = new URLSearchParams({
          from: fromStr,
          to: toStr,
        });
        // masterId optionnel : sans publish admin, on lit quand même Supabase
        if (activeMasterId) params.set("masterId", activeMasterId);
        const res = await fetch(
          `${apiBaseUrl}/api/results/daily?${params.toString()}`,
        );
        const ct = res.headers.get("content-type") || "";
        if (!responseIsJson(ct)) {
          throw new Error(
            "Daily results are temporarily unavailable. Please refresh the page and try again.",
          );
        }
        const data = await res.json();
        if (!res.ok || !data?.ok) {
          throw new Error(data?.message || "Unable to load daily results.");
        }
        if (isMounted) {
          setDailyRows(data.rows || []);
          if (data.published === false) {
            setDailyMessage(
              "Performance data for this account has not been published yet.",
            );
          } else if (data.degraded && data.message) {
            setDailyMessage(data.message);
          } else {
            setDailyMessage("");
          }
        }
      } catch (err) {
        if (isMounted) {
          setDailyMessage(
            clientSafeMessage(err?.message, "Unable to load daily results."),
          );
        }
      } finally {
        if (isMounted) {
          setDailyLoading(false);
        }
      }
    }
    loadDaily();
    return () => {
      isMounted = false;
    };
  }, [activeMasterId]);

  const tableMonthBounds = useMemo(() => {
    const max = currentUtcMonth();
    const first = firstMonthWithTradeActivityUtc(dailyRows);
    const min = first || max;
    let minK = monthKeyUtc(min);
    const maxK = monthKeyUtc(max);
    if (minK > maxK) minK = maxK;
    return { min: utcMonthFromKey(minK), max };
  }, [dailyRows]);

  useEffect(() => {
    setTableMonthUtc((prev) => {
      const k = monthKeyUtc(prev);
      const minK = monthKeyUtc(tableMonthBounds.min);
      const maxK = monthKeyUtc(tableMonthBounds.max);
      const nk = Math.max(minK, Math.min(maxK, k));
      return utcMonthFromKey(nk);
    });
  }, [tableMonthBounds]);

  // Si le mois courant est vide, ouvre le dernier mois qui a des trades (comme en local utile).
  useEffect(() => {
    if (didAutoJumpMonthRef.current) return;
    if (dailyLoading) return;
    if (!dailyRows?.length) return;
    const current = currentUtcMonth();
    if (monthHasTradeActivityUtc(dailyRows, current)) {
      didAutoJumpMonthRef.current = true;
      return;
    }
    const latest = latestMonthWithTradeActivityUtc(dailyRows);
    if (!latest) return;
    didAutoJumpMonthRef.current = true;
    setTableMonthUtc(latest);
  }, [dailyLoading, dailyRows]);

  const shiftPerformanceTableMonth = useCallback(
    (delta) => {
      setTableMonthUtc((prev) => {
        const nk = monthKeyUtc(prev) + delta;
        const minK = monthKeyUtc(tableMonthBounds.min);
        const maxK = monthKeyUtc(tableMonthBounds.max);
        const k = Math.max(minK, Math.min(maxK, nk));
        return utcMonthFromKey(k);
      });
    },
    [tableMonthBounds],
  );

  const activeData = useMemo(() => {
    const fromApi = activeMaster?.periods?.[activePeriod];
    const balance = activeMaster?.balanceUsd ?? summary?.balanceUsd;

    if (activePeriod === "daily") {
      const todayStats = todayOverviewFromDailyRows(dailyRows, balance);
      return { ...(fromApi || {}), ...todayStats };
    }

    if (activePeriod !== "yearly") {
      return fromApi || null;
    }
    if (!yearlySummaryFromApiIsEmpty(fromApi)) {
      return fromApi;
    }
    const derived = yearlyOverviewFromDailyRows(dailyRows, balance);
    if (derived) {
      return { ...(fromApi || {}), ...derived };
    }
    return fromApi || null;
  }, [activeMaster, summary, activePeriod, dailyRows]);

  const performanceTable = useMemo(
    () =>
      buildPerformanceTableRows(
        activePeriod,
        dailyRows,
        activePeriod === "daily" || activePeriod === "weekly"
          ? tableMonthUtc
          : undefined,
      ),
    [activePeriod, dailyRows, tableMonthUtc],
  );

  const tableMonthLabel = formatMonthYearUtcLabel(
    tableMonthUtc.y,
    tableMonthUtc.m0,
  );
  const atMinTableMonth =
    monthKeyUtc(tableMonthUtc) <= monthKeyUtc(tableMonthBounds.min);
  const atMaxTableMonth =
    monthKeyUtc(tableMonthUtc) >= monthKeyUtc(tableMonthBounds.max);
  const tableMonthIsCurrent =
    monthKeyUtc(tableMonthUtc) === monthKeyUtc(currentUtcMonth());

  const performanceSectionTitle =
    activePeriod === "daily"
      ? "Daily performance"
      : activePeriod === "weekly"
        ? "Weekly performance"
        : activePeriod === "monthly"
          ? "Monthly performance"
          : "Yearly performance";

  const firstColumnHeader =
    activePeriod === "daily"
      ? "Date"
      : activePeriod === "weekly"
        ? "Week"
        : activePeriod === "monthly"
          ? "Month"
          : "Year";

  return (
    <main className="min-h-screen bg-[#0B1437] px-5 py-6 text-white sm:py-8">
      <div className="mx-auto w-full max-w-6xl">
        <LegalPagesBrandHeader />
        <header className="mb-5 space-y-2 text-center">
          <h1 className="text-3xl font-bold sm:text-4xl">
            Our Trading Results
          </h1>
          <p className="mx-auto max-w-3xl text-sm leading-relaxed text-gray-300 sm:text-base">
            Full transparency. Every tracked period is presented with a
            performance-first and risk-aware view so you can evaluate real
            execution, not marketing promises.
          </p>
        </header>

        <div className="space-y-6">
        <div className="space-y-4">
          <section className="grid gap-4 sm:grid-cols-3">
            {performanceHighlights.map((item) => (
              <article
                key={item.label}
                className="rounded-2xl border border-white/10 bg-white/[0.04] p-5 text-center"
              >
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-brand-300">
                  {item.label}
                </p>
                <p className="mt-2 text-3xl font-extrabold text-white">{item.value}</p>
                <p className="mt-2 text-sm text-gray-300">{item.detail}</p>
              </article>
            ))}
          </section>
          <section className="grid gap-4 sm:grid-cols-3">
            {proofCards.map((card) => (
              <article
                key={card.title}
                className="rounded-xl border border-white/10 bg-[#0a102c]/70 p-4"
              >
                <h3 className="text-base font-semibold text-white">{card.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-gray-300">
                  {card.text}
                </p>
              </article>
            ))}
          </section>
        </div>

        <section className="rounded-2xl border border-white/10 bg-white/[0.04] p-6 sm:p-8">
          <div className="mb-6 flex flex-col gap-2 text-center">
            <h2 className="text-2xl font-bold text-white sm:text-3xl">
              Proof-Focused Performance Overview
            </h2>
            <p className="mx-auto max-w-3xl text-sm text-gray-300 sm:text-base">
              This section is designed for clean result review.
            </p>
          </div>

          {!isLoading && activeMaster && !resultsAreVisible ? (
            <div className="mb-5 rounded-xl border border-amber-400/30 bg-amber-500/10 px-4 py-3 text-center text-sm text-amber-100">
              Results are not published yet.
            </div>
          ) : null}

          <div className="mb-5 flex flex-wrap items-center justify-center gap-3">
            {periodOrder.map((period) => {
              const isActive = activePeriod === period;
              return (
                <button
                  key={period}
                  type="button"
                  onClick={() => setActivePeriod(period)}
                  className={`rounded-xl px-6 py-2.5 text-sm font-semibold uppercase tracking-[0.08em] transition ${
                    isActive
                      ? "bg-brand-500 text-white"
                      : "border border-white/25 bg-white/5 text-gray-200 hover:bg-white/10"
                  }`}
                >
                  {period.charAt(0).toUpperCase() + period.slice(1)}
                </button>
              );
            })}
          </div>

          <div className="mb-6 rounded-2xl border border-white/10 bg-[#0a102c]/70 p-5">
            {isLoading ? (
              <p className="text-sm text-gray-300">
                Loading performance data…
              </p>
            ) : error ? (
              <p className="text-sm text-rose-300">
                {sanitizeClientVisibleCopy(error)}
              </p>
            ) : !resultsAreVisible ? (
              <p className="text-sm text-gray-300">
                Performance metrics are not available yet.
              </p>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
                  <p className="text-xs uppercase tracking-[0.12em] text-gray-400">
                    PnL %
                  </p>
                  <p className="mt-1 text-xl font-bold text-white">
                    {formatValue(activeData?.pnlPercent, "%")}
                  </p>
                </div>
                <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
                  <p className="text-xs uppercase tracking-[0.12em] text-gray-400">
                    Win Rate
                  </p>
                  <p className="mt-1 text-xl font-bold text-white">
                    {formatValue(activeData?.winRate, "%")}
                  </p>
                </div>
                <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
                  <p className="text-xs uppercase tracking-[0.12em] text-gray-400">
                    Closed Trades
                  </p>
                  <p className="mt-1 text-xl font-bold text-white">
                    {formatValue(activeData?.trades)}
                  </p>
                </div>
                <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
                  <p className="text-xs uppercase tracking-[0.12em] text-gray-400">
                    Pips +
                  </p>
                  <p className="mt-1 text-xl font-bold text-white">
                    {formatValue(activeData?.pipsPositive)}
                  </p>
                </div>
                <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
                  <p className="text-xs uppercase tracking-[0.12em] text-gray-400">
                    Pips −
                  </p>
                  <p className="mt-1 text-xl font-bold text-white">
                    {formatValue(activeData?.pipsNegative)}
                  </p>
                </div>
                <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
                  <p className="text-xs uppercase tracking-[0.12em] text-gray-400">
                    Drawdown
                  </p>
                  <p className="mt-1 text-xl font-bold text-white">
                    {formatValue(activeData?.drawdownPercent, "%")}
                  </p>
                </div>
              </div>
            )}
            {!isLoading && !error && (
              <div className="mt-4 space-y-1 text-xs text-gray-400">
                {activeData?.periodLabel ? (
                  <p className="text-gray-300">
                    {sanitizeClientVisibleCopy(activeData.periodLabel)}
                  </p>
                ) : null}
                {activeData?.updatedAt ? (
                  <p>
                    Updated:{" "}
                    {new Date(activeData.updatedAt).toLocaleString()}
                  </p>
                ) : null}
              </div>
            )}
          </div>
        </section>

        <section className="rounded-2xl border border-white/10 bg-white/[0.04] p-6 sm:p-8">
          <h2 className="text-center text-2xl font-bold text-white sm:text-3xl">
            {performanceSectionTitle}
          </h2>
          {dailyMessage && (
            <p className="mt-3 text-center text-sm text-amber-200/95">
              {sanitizeClientVisibleCopy(dailyMessage)}
            </p>
          )}
          {(activePeriod === "daily" || activePeriod === "weekly") &&
            !dailyLoading && (
              <div className="mt-5 flex flex-col items-center gap-3 sm:flex-row sm:flex-wrap sm:justify-center">
                <div className="flex flex-wrap items-center justify-center gap-2">
                  <button
                    type="button"
                    onClick={() => shiftPerformanceTableMonth(-1)}
                    disabled={atMinTableMonth}
                    className={`rounded-xl px-4 py-2 text-sm font-semibold transition ${
                      atMinTableMonth
                        ? "cursor-not-allowed border border-white/10 bg-white/[0.02] text-gray-500"
                        : "border border-white/25 bg-white/5 text-white hover:bg-white/10"
                    }`}
                  >
                    ← Previous month
                  </button>
                  <span className="min-w-[10rem] rounded-xl border border-white/15 bg-[#0a102c]/80 px-4 py-2 text-center text-sm font-semibold text-white">
                    {tableMonthLabel}
                  </span>
                  <button
                    type="button"
                    onClick={() => shiftPerformanceTableMonth(1)}
                    disabled={atMaxTableMonth}
                    className={`rounded-xl px-4 py-2 text-sm font-semibold transition ${
                      atMaxTableMonth
                        ? "cursor-not-allowed border border-white/10 bg-white/[0.02] text-gray-500"
                        : "border border-white/25 bg-white/5 text-white hover:bg-white/10"
                    }`}
                  >
                    Next month →
                  </button>
                </div>
                {!tableMonthIsCurrent ? (
                  <button
                    type="button"
                    onClick={() => setTableMonthUtc(currentUtcMonth())}
                    className="rounded-xl bg-brand-500/90 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-500"
                  >
                    Jump to current month
                  </button>
                ) : null}
              </div>
            )}
          <div className="mt-4 overflow-x-auto rounded-xl border border-white/10">
            {dailyLoading ? (
              <p className="p-4 text-sm text-gray-400">Loading table…</p>
            ) : (performanceTable.mode === "monthly" ||
                performanceTable.mode === "yearly") &&
              performanceTable.rows.length === 0 ? (
              <p className="p-4 text-sm text-gray-400">
                No {performanceTable.mode} aggregates yet. After the sync fills your
                daily performance table, rows will appear here.
              </p>
            ) : (
              <table className="w-full min-w-[640px] text-left text-sm text-gray-200">
                <thead className="border-b border-white/10 bg-[#0a102c]/80 text-xs uppercase tracking-wide text-gray-400">
                  <tr>
                    <th className="px-3 py-3 font-semibold">{firstColumnHeader}</th>
                    <th className="px-3 py-3 font-semibold">Pips+</th>
                    <th className="px-3 py-3 font-semibold">Pips−</th>
                    <th className="px-3 py-3 font-semibold">Trades</th>
                    <th className="px-3 py-3 font-semibold">W/L</th>
                    <th className="px-3 py-3 font-semibold">Win rate</th>
                    <th className="px-3 py-3 font-semibold">PnL USD</th>
                  </tr>
                </thead>
                <tbody>
                  {performanceTable.rows.map((row) => (
                    <tr
                      key={row.rowKey}
                      className="border-b border-white/5 hover:bg-white/[0.03]"
                    >
                      <td className="px-3 py-2.5 font-medium text-white">
                        {performanceTable.mode === "daily" && row.periodLabel
                          ? new Date(
                              row.periodLabel + "T12:00:00Z",
                            ).toLocaleDateString()
                          : row.periodLabel || "—"}
                      </td>
                      <td className="px-3 py-2.5">
                        {row.pips_positive != null
                          ? Number(row.pips_positive).toFixed(2)
                          : "—"}
                      </td>
                      <td className="px-3 py-2.5">
                        {row.pips_negative != null
                          ? Number(row.pips_negative).toFixed(2)
                          : "—"}
                      </td>
                      <td className="px-3 py-2.5">
                        {row.trades_count != null ? row.trades_count : "—"}
                      </td>
                      <td className="px-3 py-2.5">
                        {row.wins_count != null && row.losses_count != null
                          ? `${row.wins_count} / ${row.losses_count}`
                          : "—"}
                      </td>
                      <td className="px-3 py-2.5">
                        {row.win_rate_percent != null
                          ? `${Number(row.win_rate_percent).toFixed(1)}%`
                          : "—"}
                      </td>
                      <td className="px-3 py-2.5">
                        {row.pnl_usd != null ? (
                          <strong
                            className={
                              Number(row.pnl_usd) > 0
                                ? "font-extrabold text-emerald-300"
                                : Number(row.pnl_usd) < 0
                                  ? "font-extrabold text-rose-300"
                                  : "font-extrabold text-gray-200"
                            }
                          >
                            {formatPnlUsd(row.pnl_usd)}
                          </strong>
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>

        <section className="rounded-2xl border border-white/10 bg-white/[0.04] p-6 sm:p-8">
          <h2 className="text-2xl font-bold text-white">What You Can Verify</h2>
          <p className="mt-2 text-sm text-gray-300 sm:text-base">
            We present data with the same objective: help you understand both
            potential returns and risk behavior before scaling.
          </p>
          <ul className="mt-4 grid gap-3 sm:grid-cols-2">
            {whatYouCanReview.map((item) => (
              <li
                key={item}
                className="rounded-xl border border-white/10 bg-[#0a102c]/60 px-4 py-3 text-sm text-gray-200"
              >
                {item}
              </li>
            ))}
          </ul>
        </section>

        <section className="rounded-2xl border border-emerald-300/25 bg-emerald-500/[0.08] p-6 text-center sm:p-8">
          <h2 className="text-2xl font-bold text-white sm:text-3xl">Ready to Start Winning?</h2>
          <p className="mx-auto mt-3 max-w-2xl text-sm leading-relaxed text-emerald-100/90 sm:text-base">
            Join AlphaSniper and activate a structured copy-trading workflow with
            transparent results and risk-first execution.
          </p>
          <div className="mt-8 flex justify-center px-2">
            <Link
              to="/SignUp"
              className="inline-flex w-full max-w-md justify-center rounded-2xl bg-brand-500 px-10 py-4 text-lg font-semibold tracking-wide text-white shadow-xl shadow-black/25 transition hover:bg-brand-600 hover:shadow-2xl active:bg-brand-700 sm:max-w-lg sm:px-14 sm:py-5 sm:text-xl"
            >
              Join Us Now
            </Link>
          </div>
        </section>

        <footer className="mt-8 border-t border-white/10 pt-5 text-center text-xs text-gray-400 sm:text-sm">
          <p>AlphaSniper 2026 © All Rights Reserved</p>
          <LegalFooterBannerAndLinks />
        </footer>
        </div>
      </div>
    </main>
  );
}
